import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { alpha, styled } from "@mui/material/styles";
import { Box, Skeleton, Stack, Typography } from "@mui/material";

import { useGlobalContext } from "../../provider";
import { FirstOutlineButton } from "../../components/buttons/buttons";
import { GET_PLATFORM_INFO } from "../../components/graphql";
import { ValidateError } from "../../utils/customError";
import { apiNotification } from "../../services/apiNotification";
import { restApi } from "../../provider/restApi";
import { toastMessage } from "../../utils";

const Balance = () => {
	const [globalState, { dispatch }]: GlobalContextType = useGlobalContext();

	const { data: resData, loading, error } = useQuery(GET_PLATFORM_INFO, {
		pollInterval: 2000,
	})

	const platformInfo: PlatformInfoObject = useMemo(() => {
		if (!!loading || !!error) return null;
		return resData.getPlatformInfo;
	}, [resData, loading, error])

	const withdrawRequest = async () => {
		try {
			if (platformInfo.minWithdraw > globalState.userData.balance) {
				throw new ValidateError(`Min withdraw amount is $${platformInfo.minWithdraw}`);
			}

			dispatch({ loading: true });
			const resData: any = await restApi.requestWithdraw();
			
			dispatch({ loading: false, userData: resData.userData });
			toastMessage("success", "Withraw request successed!");
		} catch (err: any) {
			console.log(err.message)
			dispatch({ loading: false });
			apiNotification(err, "Withdraw request failed!");
		}
	}

	return (
		<BalanceWrapper>
			<Typography variant="h3">
				Earnings & Withdrawable Balance
			</Typography>

			<Typography variant="body1" component="h6">
				The Earnings & Withdrawable Balance page provides users with a detailed overview of their financial activity on the platform. It displays the available withdrawable balance, weekly earnings, and total earnings, allowing users to track their revenue from courses and lectures. This feature ensures transparency and helps users manage their payouts efficiently.
			</Typography>

			{(!loading && !error) && (
				<EarningsGrid>
					<EarningsCard>
						<Stack flexDirection="row" gap={1} alignItems="end">
							<Typography variant="h5">Balance:</Typography>

							<Typography variant="body1">
								${globalState.userData.balance}
							</Typography>
						</Stack>

						<Typography variant="body2">
							Available withdrawable funds.
						</Typography>

						{(globalState.userData.balance >= platformInfo.minWithdraw) && (
							<FirstOutlineButton onClick={withdrawRequest}>
								<Typography variant="body1">Withdraw Request</Typography>
							</FirstOutlineButton>
						)}
					</EarningsCard>

					<EarningsCard>
						<Stack flexDirection="row" gap={1} alignItems="end">
							<Typography variant="h5">This week's Earnings:</Typography>

							<Typography variant="body1">
								${globalState.userData.weeklyEarning}
							</Typography>
						</Stack>

						<Typography variant="body2">
							Weekly earnings are updated automatically.
						</Typography>
					</EarningsCard>

					<EarningsCard>
						<Stack flexDirection="row" gap={1} alignItems="end">
							<Typography variant="h5">Total Earnings:</Typography>

							<Typography variant="body1">
								${globalState.userData.totalEarning}
							</Typography>
						</Stack>

						<Typography variant="body2">
							The total revenue generated by the account
						</Typography>
					</EarningsCard>
				</EarningsGrid>
			)}

			{(!!loading || !!error) && (
				<EarningsGrid>
					<EarningsCard>
						<Skeleton variant="rectangular" height={20} width="80%" />
						<Skeleton variant="text" height={25} />

						<Skeleton variant="rounded" height={40} />
					</EarningsCard>

					<EarningsCard>
						<Skeleton variant="rectangular" height={20} width="80%" />
						<Skeleton variant="text" height={25} />
					</EarningsCard>

					<EarningsCard>
						<Skeleton variant="rectangular" height={20} width="80%" />
						<Skeleton variant="text" height={25} />
					</EarningsCard>
				</EarningsGrid>
			)}
		</BalanceWrapper>
	)
}

const BalanceWrapper = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(2),

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
	}
}))

const EarningsGrid = styled(Box)(({ theme }) => ({
	display: 'grid',
	gap: theme.spacing(3),
	gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(2),
	}
}))

const EarningsCard = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: "column",
	gap: theme.spacing(1),
	padding: theme.spacing(3),
	backgroundColor: theme.palette.common.mainBg,
	boxShadow: `0px 0px 5px ${theme.palette.common.firstBorder}`,

	'& .MuiSvgIcon-root': {
		color: theme.palette.common.thirdColor,
	},

	h5: {
		fontWeight: 600,
	},

	p: {
		fontWeight: 500,
		color: alpha(theme.palette.common.firstColor, 0.75),
	},

	[theme.breakpoints.down("md")]: {
		padding: theme.spacing(1),

		'& .MuiSvgIcon-root': {
			fontSize: 25,
			color: theme.palette.common.thirdColor,
		},
	}
}))

export { Balance }